import { memo, useMemo } from 'react';
import { isQuickFilterActive } from 'shared/filter_pane/filterHelpers';
import { FilterPane } from 'shared/filter_pane/FilterPane';
import { QuickFilterToggle } from 'shared/filter_pane/QuickFilterToggle';
import { css } from 'styled-components';

export type QuickFilterValue = string[] | string | undefined;
export type QuickFilterState = Record<string, QuickFilterValue>;

/**
 * Configuration for a quick filter preset.
 *
 * Quick filters are preset combinations of filter values that can be toggled on/off.
 * Use discriminated union to ensure type safety between array and non-array filters.
 */
export type QuickFilterConfig<T extends QuickFilterState = QuickFilterState> = {
    id: string;
    label: string;
    title?: string;
    field: keyof T;
    disabled?: boolean;
} & (
    | {
          /** Array filter: value must be string[], isArray must be true */
          value: string[];
          isArray: true;
      }
    | {
          /** Non-array filter: value must be string, isArray false or omitted */
          value: string;
          isArray?: false;
      }
);

type QuickFilterPaneProps<T extends QuickFilterState> = Readonly<{
    id: string;
    filters: T;
    quickFilters: QuickFilterConfig<T>[];
    onChange: (filters: T) => void;
    containerStyles?: ReturnType<typeof css>;
    children?: React.ReactNode;
    filterCount?: number;
}>;

function QuickFilterPaneBase<T extends QuickFilterState>({
    id,
    filters,
    quickFilters,
    onChange,
    containerStyles,
    children,
    filterCount: externalFilterCount,
}: QuickFilterPaneProps<T>) {
    const toggledFilters = useMemo(() => {
        return quickFilters.filter((filter) => isQuickFilterActive(filter, filters));
    }, [filters, quickFilters]);

    const filterCount = externalFilterCount ?? toggledFilters.length;
    const isAllToggled = filterCount === 0;

    const resetFilters = () => {
        const resetState = Object.keys(filters).reduce(
            (acc, key) => ({
                ...acc,
                [key]: Array.isArray(filters[key]) ? [] : undefined,
            }),
            {} as T
        );
        onChange(resetState);
    };

    const toggleFilter = (config: QuickFilterConfig<T>) => () => {
        const isToggled = isQuickFilterActive(config, filters);

        if (config.isArray) {
            // Type system guarantees config.value is string[] when isArray is true
            const nextValue = !isToggled ? config.value : [];

            onChange({
                ...filters,
                [config.field]: nextValue,
            } as T);

            return;
        }

        onChange({
            ...filters,
            [config.field]: !isToggled ? config.value : undefined,
        } as T);
    };

    return (
        <FilterPane id={id} filterCount={filterCount} containerStyles={containerStyles}>
            <QuickFilterToggle
                id={`${id}-all`}
                isToggled={isAllToggled}
                onToggle={resetFilters}
            >
                All
            </QuickFilterToggle>
            {quickFilters.map((filter) => (
                <QuickFilterToggle
                    disabled={filter.disabled}
                    title={filter.title}
                    key={filter.id}
                    id={`${id}-${filter.id}`}
                    isToggled={isQuickFilterActive(filter, filters)}
                    onToggle={toggleFilter(filter)}
                >
                    {filter.label}
                </QuickFilterToggle>
            ))}
            {children}
        </FilterPane>
    );
}

const memoQuickFilterPane = memo(QuickFilterPaneBase) as typeof QuickFilterPaneBase;
export { memoQuickFilterPane as QuickFilterPane };
